import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  propertyCategories,
  amenitiesCategories,
  safetyCategories,
  rulesCategories,
  CategoryItem,
} from '../../../../CATEGORIES/Categories';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../redux/store';
import {
  upsertShortstay,
  listenToShortstayData,
} from '../../../../redux/shortstaysSlice';

const ShortForm1: React.FC = () => {
  const { listingId } = useParams<{ listingId?: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const user = useSelector((state: RootState) => state.auth.user);
  const currentListing = useSelector(
    (state: RootState) => state.shortstays.currentListing
  );

  const [selectedProperty, setSelectedProperty] = useState<number | null>(null);
  const [selectedAmenities, setSelectedAmenities] = useState<number[]>([]);
  const [selectedSafety, setSelectedSafety] = useState<number[]>([]);
  const [selectedRules, setSelectedRules] = useState<number[]>([]);
const isValid = selectedProperty !== null;


  // 🔹 Load listing if exists
  useEffect(() => {
    if (listingId) {
      dispatch(listenToShortstayData(listingId));
    }
  }, [listingId, dispatch]);

  // 🔹 Populate if editing
  useEffect(() => {
    if (currentListing) {
      setSelectedProperty(currentListing.category ?? null);
      setSelectedAmenities(currentListing.amenities ?? []);
      setSelectedSafety(currentListing.safety ?? []);
      setSelectedRules(currentListing.rules ?? []);
    }
  }, [currentListing]);

  // 🔹 Handle checkbox toggle
  const handleToggle = (
    id: number,
    list: number[],
    setList: React.Dispatch<React.SetStateAction<number[]>>
  ) => {
    setList((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  // 🔹 Proceed to next form
  const handleNext = async () => {
    console.log('🧩 handleNext triggered', { listingId, user });

    if (!user?.userId || selectedProperty === null) {
      console.warn('⚠️ Missing userId or property type');
      return;
    }

    // Editing existing
    if (listingId) {
      console.log('✏️ Updating existing listing:', listingId);
      navigate(`/shortform2/${user.userId}/${listingId}`);
      return;
    }

    // Creating new
    console.log('🚀 Creating new shortstay listing...');
    const result = await dispatch(
      upsertShortstay({
        data: {
          category: selectedProperty,
          amenities: selectedAmenities,
          safety: selectedSafety,
          rules: selectedRules,
          name: 'Untitled Shortstay Listing',
        },
      })
    );

    console.log('🧾 upsertShortstay result:', result);

    if (upsertShortstay.fulfilled.match(result)) {
      const newListingId = result.payload.listingId;
      console.log('✅ New shortstay created:', newListingId);
      navigate(`/shortform2/${user.userId}/${newListingId}`);
    } else {
      console.error('❌ Failed to create shortstay:', result);
    }
  };

  const handleBack = () => {
    navigate('/ShortstayOverview');
  };

  // 🔹 Render grid category cards
  const renderCategory = (
    title: string,
    items: CategoryItem[],
    selected: number[] | number | null,
    onClick: (id: number) => void,
    isSingle = false
  ) => (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>
      <div style={styles.grid}>
        {items.map((item) => {
          const isSelected = Array.isArray(selected)
            ? selected.includes(item.id)
            : selected === item.id;

          return (
            <div
              key={`${title}-${item.id}`} // ✅ unique per section
              onClick={() => onClick(item.id)}
              style={{
                ...styles.card,
                background: isSelected
                  ? 'linear-gradient(to right, #111, #bf9b30)'
                  : '#fff',
                color: isSelected ? '#fff' : '#000',
                border: isSelected ? '2px solid #bf9b30' : '1px solid #ccc',
              }}
            >
              <div style={styles.icon}>{item.icon}</div>
              <p>{item.name}</p>
            </div>
          );
        })}
      </div>
    </div>
  );

  // 🔹 Render UI
  return (
    <div style={styles.container}>
      {renderCategory(
        'Short Stay Type',
        propertyCategories,
        selectedProperty,
        setSelectedProperty,
        true
      )}
      {renderCategory(
        'Amenities',
        amenitiesCategories,
        selectedAmenities,
        (id) => handleToggle(id, selectedAmenities, setSelectedAmenities)
      )}
      {renderCategory(
        'Safety',
        safetyCategories,
        selectedSafety,
        (id) => handleToggle(id, selectedSafety, setSelectedSafety)
      )}
      {renderCategory(
        'Rules',
        rulesCategories,
        selectedRules,
        (id) => handleToggle(id, selectedRules, setSelectedRules)
      )}

      <footer style={styles.footer}>
        <button onClick={handleBack} style={styles.backBtn}>
          Back
        </button>
       <button
  onClick={handleNext}
  disabled={!isValid}
  style={{
    ...styles.nextBtn,
    ...( !isValid && styles.nextBtnDisabled )
  }}
>
  NEXT
</button>
      </footer>
    </div>
  );
};

export default ShortForm1;

// ---------- Styles ----------
const styles: { [key: string]: React.CSSProperties } = {
  container: {
    backgroundColor: '#000',
    color: '#fff',
    padding: 20,
    paddingBottom: 100,
    minHeight: '100vh',
  },
  section: {
    marginBottom: 40,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 16,
  },
  card: {
    background: '#fff',
    color: '#000',
    padding: 16,
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 0 12px rgba(255,255,255,0.1)',
    border: '1px solid #ccc',
  },
  icon: {
    fontSize: 36,
    marginBottom: 8,
  },
  footer: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#000',
    borderTop: '1px solid #333',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '15px 30px',
    zIndex: 1000,
  },
  backBtn: {
    padding: '10px 20px',
    borderRadius: 30,
    backgroundColor: '#222',
    color: '#fff',
    border: '1px solid #555',
    cursor: 'pointer',
  },
  nextBtn: {
    padding: '10px 20px',
    borderRadius: 30,
    background: 'linear-gradient(to right, #fff, #bf9b30)',
    color: '#000',
    border: 'none',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  nextBtnDisabled: {
  background: '#555',
  color: '#999',
  cursor: 'not-allowed',
  opacity: 0.7,
},
};
